import { createAsyncThunk } from "@reduxjs/toolkit";
import { login as loginApi, sendPhoneOtp as sendPhoneOtpApi, verifyPhoneOtp as verifyPhoneOtpApi } from "../../../api/auth";
import { setLoginCookies, getRefreshToken, resetDataAndLogout } from "../../../auth/session";
import { getTokenPayload, isTokenExpired, decodeToken } from "../../../utils/jwt";
import { getCookie } from "../../../utils/cookie";

export const loginUserAction = createAsyncThunk(
  "auth/loginUserAction",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const data = await loginApi({ email, password });
      setLoginCookies(data);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || "Login failed");
    }
  }
);

export const sendPhoneOtp = createAsyncThunk(
  "auth/sendPhoneOtp",
  async ({ phone, agreementNumber }, { rejectWithValue }) => {
    try {
      const data = await sendPhoneOtpApi({ phone, agreementNumber });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || "Failed to send OTP");
    }
  }
);

export const loginByPhoneOtp = createAsyncThunk(
  "auth/loginByPhoneOtp",
  async ({ phone, otp }, { rejectWithValue }) => {
    try {
      const data = await verifyPhoneOtpApi({ phone, otp });
      setLoginCookies(data);
      const decoded = decodeToken(data.accessToken || data.token);
      return {
        ...data,
        user: decoded ? buildUserFromPayload(decoded) : null,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || "OTP verification failed");
    }
  }
);

const buildUserFromPayload = (payload) => {
  return {
    id: payload.id,
    name: payload.name || payload.respondentName || "",
    email: payload.email,
    phone: payload.phone,
    userRole: payload.userRole || payload.type,
    userLogID: payload.userLogID,
    userOrganization: payload.userOrganization,
    orgTypeID: payload.orgTypeID,
    orgAccess: payload.orgAccess,
    respondentDetailsIDs: payload.respondentDetailsIDs,
    caseDetailsIDs: payload.caseDetailsIDs,
    respondentName: payload.respondentName || payload.name || "",
  };
};

export const hydrateUser = createAsyncThunk(
  "auth/hydrateUser",
  async (_, { rejectWithValue }) => {
    try {
      if (!isTokenExpired()) {
        const payload = getTokenPayload();
        if (!payload) return null;
        return buildUserFromPayload(payload);
      }

      const refreshToken = getCookie("refreshToken");
      if (!refreshToken) return null;

      const newAccessToken = await getRefreshToken();
      if (newAccessToken) {
        const payload = getTokenPayload();
        if (payload) {
          return buildUserFromPayload(payload);
        }
      }

      return null;
    } catch (error) {
      return rejectWithValue(error.message || "Hydration failed");
    }
  }
);

export const logoutUserAction = createAsyncThunk(
  "auth/logoutUserAction",
  async (_, { rejectWithValue }) => {
    try {
      resetDataAndLogout();
      return true;
    } catch (error) {
      return rejectWithValue(error.message || "Logout failed");
    }
  }
);

// Backward-compatible aliases (pages use these names)
export const loginUser = loginUserAction;
export const logoutUser = logoutUserAction;

export const sendOtp = sendPhoneOtp;
export const loginByOtp = loginByPhoneOtp;
